import { useEffect } from 'react';
import { useAtom } from 'ximple';
import { octoCartAtom } from './octoCartAtom';
import { useOctoOrder } from '@repo/widget-utils/services/hooks/octo/index';
import { octoOrderAtom, setIsLoading, setOctoOrder } from './octoOrderAtom';
import { clearOctoCartEvent, setOctoCartCleanEvent } from './octoCart.reducer';
import { OctoOrder } from '@repo/types';
import { extendOctoOrder } from '@repo/widget-utils/services/fetchers/octo-api';
import { tzdate, TZDate } from '@repo/tzdate';

export function useOctoOrderEffects() {
    const [cart] = useAtom(octoCartAtom);
    const [octoOrderState] = useAtom(octoOrderAtom);

    const {
        data: octoOrderFromServer,
        isLoading,
        mutate: mutateOctoOrder,
        error,
    } = useOctoOrder(cart.id);

    useEffect(() => {
        if (!octoOrderFromServer) return;

        // when we get a new octo order from server, we need to update our octo order state
        if (octoOrderFromServer.status === 'ON_HOLD') {
            octoOrderAtom.update(setOctoOrder(octoOrderFromServer));
            octoCartAtom.update(setOctoCartCleanEvent());
        } else {
            octoCartAtom.update(clearOctoCartEvent());
        }
    }, [octoOrderFromServer]);

    useEffect(() => {
        const validateAndKeepAliveNow = async () => {
            await validateAndKeepAlive(octoOrderState.octoOrder);
        };

        // When the octo order state changes, we validate and keep it alive.
        validateAndKeepAliveNow();

        // And we keep validating it every 10 seconds
        const interval = setInterval(async () => {
            if (!octoOrderState.octoOrder) return;

            await validateAndKeepAlive(octoOrderState.octoOrder);
        }, 10 * 1000);

        return () => {
            clearInterval(interval);
        };
    }, [octoOrderState]);

    useEffect(() => {
        // Reset octoOrder if cart is empty.
        if (!cart.id) {
            octoOrderAtom.update(setOctoOrder(undefined));
        } else {
            const idsDifferent = octoOrderState.octoOrder?.id !== cart.id;
            const isDirty = cart.isDirty || idsDifferent;
            if (isDirty) {
                mutateOctoOrder();
            }
        }
    }, [cart, mutateOctoOrder, octoOrderState.octoOrder?.id]);

    useEffect(() => {
        if (error) {
            octoCartAtom.update(clearOctoCartEvent());
        }
    }, [error]);

    useEffect(() => {
        octoOrderAtom.update(setIsLoading(isLoading));
    }, [isLoading]);
}

async function validateAndKeepAlive(octoOrder?: OctoOrder) {
    if (!octoOrder) return;

    // Clear the octo cart if the order is not on hold (i.e., it's been confirmed or cancelled)
    if (octoOrder.status !== 'ON_HOLD') {
        await octoCartAtom.update(clearOctoCartEvent());
    }

    // If the order is still on hold, we need to make sure it doesn't expire
    const now = TZDate.now();
    if (tzdate(octoOrder.expiresAt, false).diff(now, 'minutes') < 5) {
        const extendedOrder = await extendOctoOrder(octoOrder.id, { expirationMinutes: 15 });
        octoOrderAtom.update(setOctoOrder(extendedOrder));
    }
}
